import FormCheckBox from '@/views/components/Inputs/FormCheckBox';
import { NOTIFICATION_TYPES, showNotification } from '@/views/components/Modals/notification';
import { Col, Form, Row } from 'antd';
import i18n from 'i18next';
import { useMemo, useState } from 'react';
import Files from 'react-files';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ReactComponent as DownloadIcon } from '../../../../../../assets/icons/download-white.svg';
import { MODULES_ID } from '../../../../../../contants/common';
import { setPageBlocker } from '../../../../../../redux/common/CommonActions';
import { selectIsPageBlockerActive } from '../../../../../../redux/common/CommonReducer';
import { valueToString } from '../../../../../../utilz';
import { routeFileSelector } from '../../../../../../utilz/i18n';
import Button from '../../../../../components/Buttons/Button';
import IconButton from '../../../../../components/Buttons/IconButton';
import SvgConverter from '../../../../../components/Common/SvgConverter';
import FormInput from '../../../../../components/Inputs/FormInput';
import HiddenFormInput from '../../../../../components/Inputs/HiddenFormInput';
import SecondaryLabel from '../../../../../components/Labels/SecondaryLabel';
import Paper from '../../../../../components/Paper/Paper';
import DocumentTypeSelector from '../../../../../components/Selectors/DocumentTypeSelector';
import TMSFiledCard from '../../../../common/components/TMSFiledCard';
import SaveCancelButtons from '../../SaveCancelButtons';

const TMSFilesDetailsCard = ({
  isNew,
  fileDetails,
  handleSave,
  handleDelete,
  index,
  allowedExtentions,
  handleSaveForAddNewScreen,
  isPartialSave,
  showVisibilityControls,
}) => {
  const [editMode, setEditMode] = useState(Boolean(fileDetails.isNew));
  const [file, setFile] = useState(false);
  const [form] = Form.useForm();
  const [isModified, setIsModified] = useState(false);
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);

  const handleSubmit = (data) => {
    setIsModified(false);
    if (handleSaveForAddNewScreen) {
      handleSaveForAddNewScreen(data, file, index);
      setEditMode(false);
    } else {
      const formData = new FormData();
      formData.append('DocumentTitle', valueToString(data.documentTitle));
      formData.append('ReferenceNo', valueToString(data.referenceNo));
      formData.append('DocumentTypeCode', data.docTypeCode);
      formData.append('DocumentDesc', data.documentDesc);
      formData.append('isCarrierVisible', !!data.isCarrierVisible);
      formData.append('isVendorVisible', !!data.isVendorVisible);
      if (file) {
        formData.append('File', file);
      } else if (data.file) {
        formData.append('file', data.file);
      }
      handleSave(formData, isNew, index);
    }
  };

  const handleFileChange = (files) => {
    try {
      if (files.length > 0) {
        setFile(files[0]);
        form.setFieldsValue({ file: files[0].name });
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleFileError = (error) => {
    try {
      if (error) {
        form.setFields([
          {
            name: 'file',
            errors: [error.message],
          },
        ]);
      }
    } catch (error) {
      console.log(error);
    }
  };
  const handleReset = () => {
    setIsModified(false);
    form.setFieldsValue({
      docTypeCode: fileDetails.fileType,
      referenceNo: fileDetails.referenceNo,
      documentTitle: fileDetails.fileComments,
      documentDesc: fileDetails.fileDescription,
      isCarrierVisible: fileDetails.isCarrierVisible,
      isVendorVisible: fileDetails.isVendorVisible,
    });
    setEditMode(false);
  };

  const fileName = Form.useWatch('file', form);
  const documentDesc = Form.useWatch('documentDesc', form);
  const dispatch = useDispatch();
  const isPageBlockerActive = useSelector(selectIsPageBlockerActive);

  return (
    <Form
      form={form}
      onFinish={handleSubmit}
      initialValues={
        fileDetails?.fileGuid
          ? {
              docTypeCode: fileDetails.fileType,
              referenceNo: fileDetails.referenceNo,
              documentTitle: fileDetails.fileComments,
              documentDesc: fileDetails.fileDescription,
              file: fileDetails.file,
              isCarrierVisible: fileDetails.isCarrierVisible,
              isVendorVisible: fileDetails.isVendorVisible,
            }
          : {
              isCarrierVisible: true,
              isVendorVisible: true,
            }
      }
      onFieldsChange={() => {
        if (!isPageBlockerActive) {
          setIsModified(true);
          dispatch(setPageBlocker(true));
        }
      }}
    >
      <Paper className="border-muted mb-16">
        <div className="border-bottom-muted px-16 pt-8 pb-10">
          <Row justify="space-between" align="middle">
            <Col>
              <Row gutter={5} align="middle">
                <Col>
                  <SecondaryLabel className="f-12">{i18n.t('translation:default.fields.fileName')}: </SecondaryLabel>
                </Col>

                <Col>
                  {editMode && allowedExtentions && (
                    <Files
                      className="files-dropzone"
                      onChange={handleFileChange}
                      onError={handleFileError}
                      accepts={allowedExtentions}
                      multiple={false}
                      minFileSize={0}
                      clickable
                    >
                      <Button>{t('translation:default.documentManager.chooseFile')}</Button>
                    </Files>
                  )}
                  <HiddenFormInput
                    name="file"
                    validateStatus=""
                    rules={[
                      {
                        required: true,
                        message: t('translation:default.documentManager.chooseFileMessage'),
                      },
                      {
                        validator: (_, value) => {
                          const validName = /^[\w,\s()-]+\.[A-Za-z]{1,5}$/; // Allows letters, numbers, spaces, hyphens, underscores,
                          if (value && !validName.test(value)) {
                            showNotification({
                              message: `'FileName' Special Characters like , [ # % & * : < > ? / ] are not allowed in the file name.`,
                              type: NOTIFICATION_TYPES.ERROR,
                            });
                            return Promise.reject();
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  />
                </Col>

                <Col>
                  <div className="f-14 fw-600">{fileName}</div>
                </Col>
              </Row>
            </Col>
            <Col>
              <Row gutter={16} align="middle">
                {fileDetails?.cloudUrl?.length > 0 && (
                  <Col>
                    <a target="_blank" href={fileDetails?.cloudUrl?.[0]} rel="noreferrer">
                      <IconButton>
                        <SvgConverter Icon={DownloadIcon} color="header" />
                      </IconButton>
                    </a>
                  </Col>
                )}

                <Col className="lh-0">
                  <SaveCancelButtons
                    editMode={editMode}
                    setEditMode={setEditMode}
                    onCancel={() => {
                      if (isNew) {
                        handleDelete(index, isNew);
                      } else {
                        handleReset();
                      }
                    }}
                    handleRemove={() => handleDelete(index, isNew)}
                    isNew={isNew}
                    isModified={isModified}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </div>
        <Row className="p-16" gutter={16}>
          <Col sm={6}>
            <Row>
              <Col sm={24}>
                {editMode ? (
                  <DocumentTypeSelector
                    editMode={editMode && Boolean(isNew)}
                    margin="mb-16"
                    name="docTypeCode"
                    moduleId={MODULES_ID.SHIPMENT}
                    rules={[
                      {
                        required: true,
                        message: t('translation:default.documentManager.documentManager.message'),
                      },
                    ]}
                    onChange={({ label }) => form.setFieldsValue({ documentDesc: label })}
                    disableValidation={isPartialSave}
                  />
                ) : (
                  <TMSFiledCard
                    containerClass="mt-0"
                    isRequired
                    title={t('translation:default.documentManager.documentManager.label')}
                  >
                    {documentDesc}
                  </TMSFiledCard>
                )}
                <HiddenFormInput name="docTypeCode" />
              </Col>
            </Row>
          </Col>
          <Col sm={6}>
            <Row>
              <Col sm={24}>
                <FormInput
                  label={t('translation:shipmentAdd.documentDetails.supportingDocument.label')}
                  editMode={editMode}
                  margin="mb-0"
                  name="referenceNo"
                  maxLength={50}
                />
              </Col>
            </Row>
          </Col>
          <Col sm={6}>
            <Row>
              <Col sm={24}>
                <FormInput
                  label={t('translation:default.documentManager.fileComments.label')}
                  editMode={editMode}
                  margin="mb-0"
                  name="documentTitle"
                />
              </Col>
            </Row>
          </Col>
        </Row>
        <Row className="pt-0 pr-16 pl-16 pb-16" gutter={16}>
          {showVisibilityControls && (
            <Col sm={6}>
              <Row className="mt-10">
                <Col sm={24} className="pt-5">
                  <FormCheckBox
                    disabled={!editMode}
                    checkBoxClass="normal-checkbox"
                    name="isCarrierVisible"
                    label={t('translation:default.documentManager.isCarrierVisible.label')}
                    className="mb-10"
                  />
                </Col>
                <Col sm={24}>
                  <FormCheckBox
                    disabled={!editMode}
                    checkBoxClass="normal-checkbox"
                    name="isVendorVisible"
                    label={t('translation:default.documentManager.isVendorVisible.label')}
                    className="mb-0"
                  />
                </Col>
              </Row>
            </Col>
          )}
        </Row>
      </Paper>
      <HiddenFormInput name="documentDesc" />
    </Form>
  );
};

export default TMSFilesDetailsCard;
